<template>
	<div class="widget widget-kpi widget-sps" :class="{ 'fullscreen-widget': isFullscreen, [params.shape]: params.shape }" ref="widgetRootRef">
		<v-card class="mx-auto" flat>
			<template v-if="!isLoading">
				<v-btn v-if="isSheetLayout" class="widget-back-fab" icon variant="flat" :aria-label="t('Back')" @click="toggleFullscreen">
					<v-icon>chevron_left</v-icon>
				</v-btn>

				<v-toolbar color="transparent" density="compact" @click="toggleFullscreen" v-if="isFullscreen">
					<div class="v-card-top-actions" v-if="!isSheetLayout">
						<span>
							<v-icon>chevron_left</v-icon>
							{{ t('Back') }}
						</span>
					</div>
					<v-toolbar-title>
						{{ title }}
					</v-toolbar-title>
					<div class="v-card-top-actions"></div>
				</v-toolbar>

				<v-card-text>
					<Kpi v-show="!isFullscreen" :title="title" color="primary" :width="12" :params="miniParams" :static-data="overviewStaticData" :guid="guid" :show-difference="true" @click="toggleFullscreen" />
					<SpsBuilder v-if="isFullscreen" :title="title" :params="params" :guid="guid" />
				</v-card-text>
			</template>

			<template v-else>
				<v-card-text>
					<div class="kpi-container">
						<v-progress-circular indeterminate color="primary" :width="12"></v-progress-circular>
					</div>
				</v-card-text>
			</template>
		</v-card>
	</div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useTranslator } from '@/composables/useTranslator';
import { eventBus } from '@/utils/core/eventBus';
import { deviceUtils } from '@/utils/core/deviceUtils';
import { spsService } from '@/services/spsService';
import Kpi from '@/components/kpi/Kpi.vue';
import SpsBuilder from '@/components/sps/SpsBuilder.vue';

const t = useTranslator();

const props = defineProps({
	title: String,
	params: Object,
	guid: String,
});

const isFullscreen = ref(false);
const isTouchMode = ref(deviceUtils.isTouchMode());
const isSheetLayout = computed(() => isFullscreen.value && isTouchMode.value);
const widgetRootRef = ref(null);
const isLoading = ref(true);
const overview = ref(null);

const onResize = () => {
	isTouchMode.value = deviceUtils.isTouchMode();
};

const miniParams = computed(() => ({ type: 'sps', shape: props.params?.shape || 'circle' }));

const overviewStaticData = computed(() => ({
	value: overview.value?.value ?? 0,
	formattedValue: overview.value?.formattedValue ?? '0',
	percentage: overview.value?.percentage ?? 0,
	difference: overview.value?.difference ?? 0,
	formattedDifference: overview.value?.formattedDifference ?? '0',
	classColor: overview.value?.classColor ?? '',
	color: 'primary',
	objective: '',
}));

onMounted(() => {
	loadOverview();
	eventBus.on('main-sync-completed', loadOverview);
	eventBus.on('background-sync-completed', loadOverview);
	window.addEventListener('appointments-updated', loadOverview);
	window.addEventListener('resize', onResize);
});

onBeforeUnmount(() => {
	eventBus.off('main-sync-completed', loadOverview);
	eventBus.off('background-sync-completed', loadOverview);
	window.removeEventListener('appointments-updated', loadOverview);
	window.removeEventListener('resize', onResize);
});

function filtersFromParams() {
	return {
		organisations: props.params?.organisations || [],
		accounts: props.params?.accounts || [],
		channels: props.params?.channels || [],
		deliveryLocationTypes: props.params?.deliveryLocationTypes || props.params?.dlts || [],
	};
}

async function loadOverview() {
	try {
		overview.value = await spsService.getSpsOverview(filtersFromParams());
	} finally {
		isLoading.value = false;
	}
}

function toggleFullscreen() {
	isFullscreen.value = !isFullscreen.value;

	eventBus.emit('widget-fullscreened', {
		widget: 'widget-sps',
		fullScreened: isFullscreen.value,
	});
}

defineExpose({
	toggleFullscreen,
	isFullscreen,
});
</script>
